import React, { useState, useEffect } from 'react';
import { getStoredTheme, applyTheme, type Theme } from '../../shared/themeUtils';
import { tenantApi } from '../../services/tenantApi';
import { DateTimePreferenceControls } from '../../shared/DateTimePreferenceControls';
import { resolveDateTimeSettings } from '../../shared/dateTimeFormatting';
import type { HourCycle } from '../../shared/dateTimePreferences';

export const ProfileSection: React.FC<{
  user: any;
  onUserUpdated: () => void;
}> = ({ user, onUserUpdated }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'appearance'>('profile');
  const [theme, setTheme] = useState<Theme>(getStoredTheme);

  const handleThemeChange = (t: Theme) => {
    setTheme(t);
    applyTheme(t);
  };
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    phone: user?.phone || '',
    timezone: resolveDateTimeSettings(user || {}).timezone,
    dateTimeLocale: (user?.dateTimeLocale || null) as string | null,
    hourCycle: (user?.hourCycle || null) as HourCycle | null,
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        displayName: user.displayName || '',
        phone: user.phone || '',
        timezone: resolveDateTimeSettings(user).timezone,
        dateTimeLocale: user.dateTimeLocale || null,
        hourCycle: user.hourCycle || null,
      });
    }
  }, [user]);

  const timezones = [
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
    'Australia/Perth', 'Australia/Adelaide',
    'Pacific/Auckland', 'America/Toronto',
    'UTC',
    'America/New_York', 'America/Los_Angeles', 'America/Chicago',
    'Europe/London', 'Europe/Amsterdam', 'Europe/Paris',
    'Asia/Singapore', 'Asia/Tokyo', 'Asia/Hong_Kong',
  ];

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await tenantApi.updateProfile(profileData);
      await onUserUpdated();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (): string => {
    const name = profileData.displayName || user?.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="profile-page">
      <div className="section-header">
        <h2>Profile Settings</h2>
      </div>

      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`profile-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
        <button
          className={`profile-tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          Appearance
        </button>
      </div>

      <div className="profile-content">
        {success && <div className="success-message">Settings saved successfully!</div>}
        {error && <div className="error-message">{error}</div>}

        {activeTab === 'profile' && (
          <div className="profile-card">
            <div className="profile-avatar-section">
              <div className="profile-avatar">{getInitials()}</div>
              <div className="profile-avatar-info">
                <h3>{profileData.displayName || user?.email}</h3>
                <p>{user?.email}</p>
                <span className="role-badge">Platform Admin</span>
              </div>
            </div>

            <div className="profile-form">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                  placeholder="Your display name"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                />
                <span className="help-text">Managed by Microsoft Entra ID</span>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+61 400 000 000"
                  />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <select
                    value={profileData.timezone}
                    onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
                  >
                    {Array.from(new Set([...timezones, profileData.timezone])).map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>

              <DateTimePreferenceControls value={profileData} onChange={preferences => setProfileData(previous => ({ ...previous, ...preferences }))} />

              <div className="form-actions">
                <button onClick={handleSave} className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="profile-card">
            <h3>Authentication</h3>
            <div className="security-item">
              <div className="security-item-info">
                <strong>Microsoft Entra ID (SSO)</strong>
                <p>Authenticated via organizational single sign-on</p>
              </div>
              <span className="status-badge active">Active</span>
            </div>

            <h3>Sessions</h3>
            <div className="security-item">
              <div className="security-item-info">
                <strong>Current Session</strong>
                <p>Active now</p>
              </div>
              <span className="status-badge active">This device</span>
            </div>
          </div>
        )}
        {activeTab === 'appearance' && (
          <div className="profile-card">
            <h3>Theme</h3>
            <p className="help-text">Choose your preferred colour scheme</p>
            <div className="theme-options">
              <button
                className={`theme-option${theme === 'light' ? ' active' : ''}`}
                onClick={() => handleThemeChange('light')}
              >
                <span className="theme-icon">☀️</span>
                <span>Light</span>
              </button>
              <button
                className={`theme-option${theme === 'dark' ? ' active' : ''}`}
                onClick={() => handleThemeChange('dark')}
              >
                <span className="theme-icon">🌙</span>
                <span>Dark</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
